import React, { useEffect, useReducer, useContext } from "react";
import "../../common/Insights.scss";
import OpenDrillDownView from "../../common/OpenDrillDownView";
import { openFlyout } from "../../../static/js/commons";
import { sendRequest } from "../../../common/http";
import {
  WmProgressIndicator,
  WmProgressMonitor,
  WmProgressSlice
} from "@watermarkinsights/ripple-react";
import translate from "../../../i18n/translate";
import { useIntl } from "react-intl";
import ApiError from "@/features/common/elements/ApiError";
import { AppContext, AppContextInterface } from "../../../contexts/AppContext";
import { useFormatMessage } from "@/features/common/helper";

const popover = (amount: number, total: number) => {
  return `${amount}/${total}`;
};

export interface CourseProgressCardProps {
  onTabLoad: (tab: string) => void;
  onComponentLoad: (type: string) => void;
}

const initialState = {
  isDrilldownOpen: false,
  drilldownType: "",
  apiError: false,

  courseData: {
    totalCourses: 0,
    courseIloNotStarted: 0,
    courseIloComplete: 0,
    courseLearningOutcomesNotStarted: 0,
    courseLearningOutcomesComplete: 0
  }
};

interface CourseData {
  totalCourses: number;
  courseIloNotStarted: number;
  courseIloComplete: number;
  courseLearningOutcomesNotStarted: number;
  courseLearningOutcomesComplete: number;
}

interface CourseProgressCardState {
  isDrilldownOpen: boolean;
  drilldownType: string;
  apiError: boolean;
  courseData: CourseData;
}

interface MissionOutcomeCourse {
  courseIloComplete: number;
  courseIloNotStarted: number;
  courseLearningOutcomesComplete: number;
  courseLearningOutcomesNotStarted: number;
  totalCourses: number;
}

interface CourseProgressCardAction {
  type: string;
  segment: string;
  missionOutcomeCourse: MissionOutcomeCourse;
  apiError: boolean;
}

const defaultCourseProgressCardAction = {
  type: "",
  segment: "",
  missionOutcomeCourse: {
    courseIloComplete: 0,
    courseIloNotStarted: 0,
    courseLearningOutcomesComplete: 0,
    courseLearningOutcomesNotStarted: 0,
    totalCourses: 0
  },
  apiError: false
};

const reducer = (
  state: CourseProgressCardState,
  action: CourseProgressCardAction
) => {
  switch (action.type) {
    case "ToggleDrilldown":
      return { ...state, isDrilldownOpen: !state.isDrilldownOpen };
    case "SetDrilldownType":
      return { ...state, drilldownType: action.segment };
    case "SetCourseData":
      return { ...state, courseData: action.missionOutcomeCourse };
    case "setApiError":
      return { ...state, apiError: action.apiError };
    default:
      return state;
  }
};

export default function CourseProgressCard({
  onTabLoad,
  onComponentLoad
}: CourseProgressCardProps) {
  const { outcomesTerminology } =
    useContext<AppContextInterface>(AppContext);
  const POPOVER_BUTTON_TEXT = useIntl().formatMessage({
    id: "app.courseCard.progressDrilldownCourse"
  });
  const DD_SUB_HEADER = useIntl().formatMessage({
    id: "app.orgProgressCard.missionOutcome"
  });

  const [state, dispatch] = useReducer(reducer, initialState);
  const formatMessage = useFormatMessage();

  const setIsDrilldownOpen = () => {
    dispatch({
      ...defaultCourseProgressCardAction,
      type: "ToggleDrilldown"
    });
  };

  const openDrilldownView = (segment: string) => {
    setIsDrilldownOpen();
    dispatch({
      ...defaultCourseProgressCardAction,
      type: "SetDrilldownType",
      segment: segment
    });
  };

  useEffect(() => {
    const applyResult = (missionOutcomeCourse: MissionOutcomeCourse) => {
      dispatch({
        ...defaultCourseProgressCardAction,
        type: "SetCourseData",
        missionOutcomeCourse: missionOutcomeCourse
      });
      onTabLoad("courses");
      onComponentLoad("courses");
    };

    const onError = () => {
      dispatch({
        ...defaultCourseProgressCardAction,
        type: "setApiError",
        apiError: true
      });
      onTabLoad("courses");
      onComponentLoad("courses");
    };
    sendRequest(
      { uri: "/mission_outcome/courses", camelize: true },
      applyResult,
      onError
    );
  }, []);

  const CourseProgressCardBody = (courseData: MissionOutcomeCourse) => {
    const created = useIntl().formatMessage({
      id: "app.drilldown.created"
    });

    const notCreated = useIntl().formatMessage({
      id: "app.drilldown.notCreated"
    });

    const mapped = useIntl().formatMessage({
      id: "app.drilldown.mapped"
    });

    const notMapped = useIntl().formatMessage({
      id: "app.drilldown.notMapped"
    });
    const completedPopoverText = formatMessage(
      { id: "app.courseCard.coursesILOMapped" },
      { learning_outcome_plural_lowercase: outcomesTerminology?.learningOutcomes?.plural.toLowerCase() || "..." }
    );
    
    const notStartedPopoverText = formatMessage(
      { id: "app.courseCard.coursesILONotMapped" },
      { learning_outcome_plural_lowercase: outcomesTerminology?.learningOutcomes?.plural.toLowerCase() || "..." }
    );
    
    const learningOutcomes = formatMessage(
      { id: "app.courseCard.learningOutcomes" },
      { learning_outcome_plural: outcomesTerminology?.learningOutcomes?.plural || "..." }
    );
    const iLOMapped = useIntl().formatMessage({
      id: "app.orgProgressCard.ILOMapped"
    });
    const courseChart = (): JSX.Element => {
      return (
        <WmProgressMonitor id="course-progress" bar-labels-width="5rem">
          <WmProgressIndicator label={learningOutcomes}>
            <WmProgressSlice
              legend={created}
              amount={courseData.courseLearningOutcomesComplete.toString()}
              popoverTitle={popover(
                courseData.courseLearningOutcomesComplete,
                courseData.totalCourses
              )}
              popoverText={completedPopoverText}
              // popoverButtonText={POPOVER_BUTTON_TEXT} // hide popover button as drilldown work is pending
              // onClick={() => openDrilldownView("course_learning_outcomes")} // required with popoverButtonText
            />
            <WmProgressSlice
              legend={notCreated}
              amount={courseData.courseLearningOutcomesNotStarted.toString()}
              popoverTitle={popover(
                courseData.courseLearningOutcomesNotStarted,
                courseData.totalCourses
              )}
              popoverText={notStartedPopoverText}
              // popoverButtonText={POPOVER_BUTTON_TEXT} // hide popover button as drilldown work is pending
              // onClick={() => openDrilldownView("course_learning_outcomes")} // required with popoverButtonText
            />
          </WmProgressIndicator>
          <WmProgressIndicator label={iLOMapped}>
            <WmProgressSlice
              legend={mapped}
              amount={courseData.courseIloComplete.toString()}
              popoverTitle={popover(
                courseData.courseIloComplete,
                courseData.totalCourses
              )}
              popoverText={completedPopoverText}
              // popoverButtonText={POPOVER_BUTTON_TEXT} // hide popover button as drilldown work is pending
              // onClick={() => openDrilldownView("course_ilo_mapping")} // required with popoverButtonText
            />
            <WmProgressSlice
              legend={notMapped}
              amount={courseData.courseIloNotStarted.toString()}
              popoverTitle={popover(
                courseData.courseIloNotStarted,
                courseData.totalCourses
              )}
              popoverText={notStartedPopoverText}
              // popoverButtonText={POPOVER_BUTTON_TEXT} // hide popover button as drilldown work is pending
              // onClick={() => openDrilldownView("course_ilo_mapping")} // required with popoverButtonText
            />
          </WmProgressIndicator>
        </WmProgressMonitor>
      );
    };
    return (
      <div>
        <h4 className="title">{translate("app.courseCard.courseProgress")}</h4>
        <p className="course totals">
          {translate("app.courseCard.courseTotal", {
            totalCourses: courseData.totalCourses
          })}
        </p>
        <div id="course-charts">{courseChart()}</div>
      </div>
    );
  };

  const coursesTotal = useIntl().formatMessage(
    { id: "app.courseCard.courseTotal" },
    { totalCourses: state.courseData.totalCourses.toString() }
  );

  return (
    <>
      {state.apiError ? (
        <ApiError containerClasses="api-error" />
      ) : (
        CourseProgressCardBody(state.courseData)
      )}

        <OpenDrillDownView
          setIsDrilldownOpen={setIsDrilldownOpen}
          drilldownType={state.drilldownType}
          total={coursesTotal}
          showTable={false}
          completeHierarchy={[]}
          drilldownStatus="any"
          subHeader={DD_SUB_HEADER}
          leads={[]}
          isVisible={state.isDrilldownOpen}
          open={state.isDrilldownOpen}
          id="course-progress-drill-down-view"
        ></OpenDrillDownView>
    </>
  );
}
